import { useEffect, useReducer, useCallback } from 'react';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useSelector, useDispatch } from 'react-redux';
import { openAlert } from '../store/actions/alert';
import moment from 'moment';
import { getAddress, transformGoogleAddress } from './directions.util';
import { getAirportCode } from './location.utils';
import { getDominantSearchType, hotelOptionMap, isBXservice } from './search.utils';
import { selectGeolocation } from '../store/selectors/trip.selector';
import AsyncStoreService from '../services/async-store.service';
import { getLocationDetail } from '../services/api/search.service';
import { useTranslation } from 'react-i18next';
import { useComponentTKey, i18nNS } from '../i18n';
import { getLowFares, resetPriceOnCalendar } from '../store/actions/search';
import {
  searchTypesEnum,
  subSearchTypeEnum,
  searchSubTypeToAirRequestTypeMap,
} from '../constants/Enums';
import { selectTezEvent } from '../store/selectors/events.selector';
import { EVENTS_ENABLED, AA_ENABLED } from '../config/env';
import { selectLocationDetail } from '../store/selectors/search.selector';
import {
  selectShowHomepagePopupforOneWay,
  selectHomepageOnewayPopupDetails,
} from '../store/selectors/profile.selector';
import { Modals } from '../constants/ScreenNames';

const searchCategoryMap = {
  Regions: 'Hotel Groups',
  Cities: 'City Search',
  Airports: 'Airports',
};

const criteriaShape = {
  departureAirport: '',
  departRegionId: 0,
  departParentKey: 0,
  fromCity: '',
  fromDate: '',

  arrivalAirport: '',
  arrivalRegionId: 0,
  arrivalParentKey: 0,
  toCity: '',
  toDate: '',
  carDropOffTime: '',
  isDifferentDropoff: undefined,
};

const getInitialSearchCriteria = (eventData) => {
  if (EVENTS_ENABLED) {
    if (eventData) {
      return {
        ...criteriaShape,
        toCity: eventData.AirportCd.split(',')[0],
        fromDate: moment(eventData?.FromDate).format('YYYY-MM-DD'),
        toDate: moment(eventData?.ToDate).format('YYYY-MM-DD'),
      };
    }
  } else {
    const todayMoment = moment(new Date());
    const today = todayMoment.format('YYYY-MM-DD');
    const tomorrow = todayMoment.add(1, 'days').format('YYYY-MM-DD');
    return {
      ...criteriaShape,
      fromDate: today,
      toDate: tomorrow,
    };
  }
};

const initialState = {
  hasCar: false,
  hasAir: false,
  hasHotel: true,
  hasRail: false,
  searchCriteria: [getInitialSearchCriteria()],
  dominantSearchType: 'hotel',
  subSearchType: 'oneway',
  airClassType: 'economy',
  enableSearchTypeSelector: true,
  enablePassengerClass: true,
  isGeoSearch: false,
  isGeoFromSearch: false,
  isGeoToSearch: false,
  tripOptions: {
    noOfCars: 1,
    noOfRooms: 1,
    persons: {
      noOfAdults: 1,
      noOfSeniors: 0,
      infants: [], // => array of {age: numerical,inSeat: boolean}
    },
  },
};

const rebookInitialState = {
  hasCar: false,
  hasAir: true,
  hasHotel: false,
  hasRail: false,
  searchCriteria: [getInitialSearchCriteria()],
  dominantSearchType: 'air',
  subSearchType: 'oneway',
  airClassType: 'economy',
  enableSearchTypeSelector: true,
  enablePassengerClass: true,
  isGeoSearch: false,
  isGeoFromSearch: false,
  isGeoToSearch: false,
};

const verifySearchSubType = (newDominantSearchType, subSearchType) => {
  if (newDominantSearchType === 'hotel') {
    if (!hotelOptionMap[subSearchType]) {
      return 'oneway';
    }
  }
  if (newDominantSearchType === 'car' || newDominantSearchType === 'air') {
    return 'roundtrip';
  }
  return subSearchType;
};

function reducer(state, action) {
  switch (action.type) {
    case 'setHasAir': {
      const toggledHasAir = !state.hasAir;
      const newDominantSearchType = getDominantSearchType(
        toggledHasAir,
        state.hasHotel,
        state.hasCar,
        false
      );
      const verifiedSubSearchType = verifySearchSubType(newDominantSearchType, state.subSearchType);
      // Check if Air is being turned OFF, and only Car is ON
      const shouldResetDates =
        toggledHasAir === false &&
        state.hasCar === true &&
        state.hasHotel === false &&
        state.hasRail === false;

      const newSearchCriteria = shouldResetDates
        ? state.searchCriteria.map((criteria) => ({
            ...criteria,
            carPickUpTime: '',
            carDropOffTime: '',
          }))
        : state.searchCriteria;

      return {
        ...state,
        hasAir: toggledHasAir,
        hasRail: false,
        dominantSearchType: newDominantSearchType,
        subSearchType: verifiedSubSearchType,
        searchCriteria: newSearchCriteria,
      };
    }
    case 'setHasHotel': {
      const newDominantSearchType = getDominantSearchType(
        state.hasAir,
        !state.hasHotel,
        state.hasCar,
        false
      );
      const verifiedSubSearchType = verifySearchSubType(newDominantSearchType, state.subSearchType);
      return {
        ...state,
        hasHotel: !state.hasHotel,
        hasRail: false,
        dominantSearchType: newDominantSearchType,
        subSearchType: verifiedSubSearchType,
      };
    }
    case 'setHasCar': {
      const toggledHasCar = !state.hasCar;

      const newDominantSearchType = getDominantSearchType(
        state.hasAir,
        state.hasHotel,
        toggledHasCar,
        false
      );

      const verifiedSubSearchType = verifySearchSubType(newDominantSearchType, state.subSearchType);

      // Reset if Car is being turned ON and all others are OFF
      const shouldResetDates =
        toggledHasCar === true &&
        state.hasAir === false &&
        state.hasHotel === false &&
        state.hasRail === false;

      const newSearchCriteria = shouldResetDates
        ? state.searchCriteria.map((criteria) => ({
            ...criteria,
            carPickUpTime: '',
            carDropOffTime: '',
          }))
        : state.searchCriteria;

      return {
        ...state,
        hasCar: toggledHasCar,
        hasRail: false,
        dominantSearchType: newDominantSearchType,
        subSearchType: verifiedSubSearchType,
        searchCriteria: newSearchCriteria,
      };
    }
    case 'setHasRail': {
      const newDominantSearchType = getDominantSearchType(
        state.hasAir,
        state.hasHotel,
        state.hasCar,
        !state.hasRail
      );
      const verifiedSubSearchType = verifySearchSubType(newDominantSearchType, state.subSearchType);
      return {
        ...state,
        hasRail: !state.hasRail,
        hasAir: false,
        hasHotel: false,
        hasCar: false,
        dominantSearchType: newDominantSearchType,
        subSearchType: verifiedSubSearchType,
      };
    }
    case 'setSearchSubType':
      return {
        ...state,
        subSearchType: action.subSearchType,
      };
    case 'setDepartCity': {
      const { item, index } = action;
      return {
        ...state,
        searchCriteria: state.searchCriteria.map((x, i) => {
          if (index !== i) {
            return x;
          }
          return {
            ...x,
            departureAirport: item.label,
            departRegionId: item.regionid,
            departParentKey: item.key,
            fromCity: getAirportCode(item.label),
          };
        }),
      };
    }
    case 'setArrivalCity': {
      const { item, index } = action;
      const isLast = state.searchCriteria.length - 1 === index;
      const newCriteria = state.searchCriteria.map((x, i) => {
        if (index !== i) {
          return x;
        }
        return {
          ...x,
          arrivalAirport: item.label,
          arrivalRegionId: item.regionid,
          arrivalParentKey: item.key,
          toCity: getAirportCode(item.label),
        };
      });
      if (state.subSearchType == 'multicity') {
        if (!isLast) {
          newCriteria[index + 1].fromCity = newCriteria[index].toCity;
          newCriteria[index + 1].departureAirport = newCriteria[index].arrivalAirport;
          newCriteria[index + 1].departRegionId = newCriteria[index].arrivalRegionId;
          newCriteria[index + 1].departParentKey = newCriteria[index].arrivalParentKey;
        }
      }
      return {
        ...state,
        searchCriteria: newCriteria,
      };
    }
    case 'addCity': {
      const lastCriteria = state.searchCriteria[state.searchCriteria.length - 1];
      criteriaShape.departureAirport = lastCriteria.arrivalAirport;
      criteriaShape.departRegionId = lastCriteria.arrivalRegionId;
      criteriaShape.departParentKey = lastCriteria.arrivalParentKey;
      criteriaShape.fromCity = lastCriteria.toCity;
      return {
        ...state,
        searchCriteria: [...state.searchCriteria, { ...criteriaShape }],
      };
    }
    case 'removeCity': {
      return {
        ...state,
        searchCriteria: [
          ...state.searchCriteria.filter(
            (element, index) => index < state.searchCriteria.length - 1
          ),
        ],
      };
    }
    case 'setDates': {
      const { startDate, endDate, dropOff, pickUp, index } = action;
      return {
        ...state,
        searchCriteria: state.searchCriteria.map((x, i) => {
          if (index !== i) {
            return x;
          }
          return {
            ...x,
            fromDate: startDate,
            toDate: endDate,
            carDropOffTime: dropOff,
            carPickUpTime: pickUp,
          };
        }),
      };
    }
    case 'setCategory': {
      const { index, locationDetail } = action;
      if (locationDetail) {
        const { category, key, airportcode } = locationDetail;
        const searchCategory = searchCategoryMap[category];
        const onlyHotel = state.dominantSearchType === 'hotel' && !state.hasCar;
        const hotelOrCarCategory = category === 'H' || category === 'C';
        const setHotelAirportCode = airportcode && hotelOrCarCategory && onlyHotel;

        return {
          ...state,
          searchCriteria: state.searchCriteria.map((x, i) => {
            if (index !== i) {
              return x;
            }
            return {
              ...x,
              category: searchCategory,
              categoryKey: key,
              toCity: setHotelAirportCode ? airportcode : x.toCity,
            };
          }),
        };
      }
      return {
        ...state,
        searchCriteria: state.searchCriteria.map((x, i) => {
          if (index !== i) {
            return x;
          }
          return {
            ...x,
            category: '',
            categoryKey: 0,
          };
        }),
      };
    }
    case 'setAirClassType': {
      return {
        ...state,
        airClassType: action.airClassType,
      };
    }
    case 'setTripOptions': {
      return {
        ...state,
        tripOptions: action.tripOptions,
      };
    }
    case 'setServiceType': {
      return {
        ...state,
        serviceType: action.serviceType,
      };
    }
    case 'setAuthNumber': {
      return { ...state, authNumber: action.authNumber };
    }
    case 'setFromStorage': {
      return action.searchInput;
    }

    case 'clearCities': {
      return {
        ...state,
        searchCriteria: state.searchCriteria.map((search) => {
          return {
            ...search,
            arrivalAirport: '',
            departureAirport: '',
            fromCity: '',
            toCity: '',
            departRegionId: 0,
            departParentKey: 0,
            arrivalRegionId: 0,
            arrivalParentKey: 0,
            carDropOffTime: '',
            category: '',
            categoryKey: '',
          };
        }),
      };
    }
    case 'isGeosearch': {
      return { ...state, isGeoSearch: action.isgoogle };
    }
    case 'isGeoFromSearch': {
      return { ...state, isGeoFromSearch: action.isgoogle };
    }
    case 'isGeoToSearch': {
      return { ...state, isGeoToSearch: action.isgoogle };
    }

    default:
      throw new Error('search input invalid action');
  }
}

const getInitialState = (initState, isRebook, eventData) => {
  if (isRebook) {
    return {
      ...rebookInitialState,
    };
  }
  if (EVENTS_ENABLED) {
    if (eventData) {
      let tempDominantSearchType = searchTypesEnum.AIR;
      let tempSubSearchType = subSearchTypeEnum.oneWay;
      if (eventData?.IsFlightAllowed) {
        tempDominantSearchType = searchTypesEnum.AIR;
        //set round trip if OW & RY are false
        if (
          eventData?.IsRoundTripAllowed ||
          (!eventData?.IsRoundTripAllowed && !eventData?.IsOneWayAllowed)
        ) {
          tempSubSearchType = subSearchTypeEnum.roundTrip;
        }
      } else if (eventData?.IsHotelAllowed) {
        tempDominantSearchType = searchTypesEnum.HOTEL;
        tempSubSearchType = subSearchTypeEnum.oneWay;
      } else if (eventData?.IsCarAllowed) {
        tempDominantSearchType = searchTypesEnum.CAR;
        tempSubSearchType = subSearchTypeEnum.oneWay;
      }
      return {
        hasCar: eventData?.IsCarAllowed,
        hasAir: eventData?.IsFlightAllowed,
        hasHotel: eventData?.IsHotelAllowed,
        hasRail: false,
        searchCriteria: [getInitialSearchCriteria(eventData)],
        dominantSearchType: tempDominantSearchType,
        subSearchType: tempSubSearchType,
        airClassType: 'economy',
        enableSearchTypeSelector: true,
        enablePassengerClass: true,
        isGeoSearch: false,
        isGeoFromSearch: false,
        isGeoToSearch: false,
        travelersLimit: eventData?.TravelersPerItinerary,
        tripOptions: {
          noOfCars: 1,
          noOfRooms: 1,
          persons: {
            noOfAdults: 1,
            noOfSeniors: 0,
            infants: [], // => array of {age: numerical,inSeat: boolean}
          },
        },
      };
    }
  }
  if (AA_ENABLED) {
    return {
      ...initialState,
      ...initState,
      hasAir: true,
      hasHotel: false,
      dominantSearchType: 'air',
      subSearchType: 'roundtrip',
      serviceType: initState.serviceType,
      isLeisureAllowed: initState.isLeisureAllowed,
      authNumber: '',
    };
  }
  return { ...initialState, ...initState };
};
const useTripSearchWidget = (allowRehydration, onSearch, initState, isRebook) => {
  const { t } = useTranslation(i18nNS.searchWidget);
  const { tKey } = useComponentTKey('TripSearchWidget');
  const reduxDispatch = useDispatch();
  //const [isGeoSearch, setisGeoSearch] = useState(false);
  const route = useRoute();
  const navigation = useNavigation();
  const { selectedLocation, selectedDates, selectedAirClassType, selectedTripOptions } =
    route?.params || {};

  const geolocation = useSelector(selectGeolocation);
  const locationDetail = useSelector(selectLocationDetail);
  const eventData = useSelector(selectTezEvent);
  const showHomepagePopupForOneway = useSelector(selectShowHomepagePopupforOneWay);
  const homepagePopupForOneWayDetails = useSelector(selectHomepageOnewayPopupDetails);
  const [searchInputState, dispatch] = useReducer(
    reducer,
    getInitialState(initState, isRebook, eventData)
  );
  __DEV__ && console.log({ searchInputState });
  const {
    hasAir,
    hasHotel,
    hasCar,
    hasRail,
    airClassType,
    subSearchType,
    dominantSearchType,
    searchCriteria,
  } = searchInputState;
  const firstSearchCriteria = searchCriteria[0];
  const firstFromCity = firstSearchCriteria.fromCity;
  const firstToCity = firstSearchCriteria.toCity;

  //when any of the criteria that can affect low fares is changed, update a ref to trigger a new service call
  useEffect(() => {
    if (isBXservice(searchInputState.serviceType)) {
      return;
    }

    if (
      firstToCity &&
      (firstFromCity || geolocation?.airportCode) &&
      hasAir &&
      subSearchType === subSearchTypeEnum.roundTrip
    ) {
      getLowPriceCalendar({
        firstToCity,
        firstFromCity: firstFromCity || geolocation?.airportCode,
        hasAir,
        subSearchType,
      });
    } else {
      erasePriceOnCalendar();
    }
  }, [
    getLowPriceCalendar,
    firstToCity,
    firstFromCity,
    hasAir,
    subSearchType,
    geolocation,
    searchInputState.serviceType,
  ]);

  const getLowPriceCalendar = useCallback(
    async (params) => {
      const { firstFromCity, firstToCity, hasAir, subSearchType } = params;
      if (firstFromCity && firstToCity) {
        if (hasAir && subSearchType !== 'multicity') {
          reduxDispatch(getLowFares(firstFromCity, firstToCity, 0));
        }
      }
    },
    [reduxDispatch]
  );

  const erasePriceOnCalendar = useCallback(async () => {
    reduxDispatch(resetPriceOnCalendar());
  }, [reduxDispatch]);

  useEffect(() => {
    async function handleLocationPicked(item) {
      const { source, criteriaIndex, componentType, shouldUpdateCategory } = item;
      if (source === 'from') {
        dispatch({ type: 'setDepartCity', item, index: criteriaIndex });
      } else if (source === 'to') {
        dispatch({ type: 'setArrivalCity', item, index: criteriaIndex });
      }
      if (item.category === 'G' && source === 'from') {
        dispatch({ type: 'isGeoFromSearch', isgoogle: true });
      } else if (item.category === 'G' && source === 'to') {
        dispatch({ type: 'isGeoToSearch', isgoogle: true });
      } else {
        if (source === 'from') {
          dispatch({ type: 'isGeoFromSearch', isgoogle: false });
        } else {
          dispatch({ type: 'isGeoToSearch', isgoogle: false });
        }
      }
      if (shouldUpdateCategory) {
        let locationDetail = '';
        if (item.category !== 'G') {
          //call CMS
          locationDetail = await getLocationDetail(
            item.label,
            item.category,
            componentType,
            item.regionid
          );
        }

        dispatch({ type: 'setCategory', locationDetail, index: criteriaIndex, source: source });
      }
    }
    if (selectedLocation) {
      handleLocationPicked(selectedLocation);
    }
  }, [selectedLocation]);

  useEffect(() => {
    const handleDatePicked = (item) => {
      const { startDate, endDate, dropOff, pickUp, criteriaIndex } = item;
      dispatch({ type: 'setDates', startDate, endDate, dropOff, pickUp, index: criteriaIndex });
    };
    if (selectedDates) {
      handleDatePicked(selectedDates);
    }
  }, [selectedDates]);

  useEffect(() => {
    if (selectedAirClassType) {
      dispatch({ type: 'setAirClassType', airClassType: selectedAirClassType });
    }
  }, [selectedAirClassType]);

  useEffect(() => {
    if (selectedTripOptions) {
      dispatch({ type: 'setTripOptions', tripOptions: selectedTripOptions });
    }
  }, [selectedTripOptions]);

  useEffect(() => {
    async function getExistingSearchInput() {
      const searchInput = await AsyncStoreService.getSearchInput();
      if (searchInput) {
        dispatch({ type: 'setFromStorage', searchInput });
      }
    }
    //todo figure out how to run something one time while depending on props without eslint complain
    if (allowRehydration) {
      getExistingSearchInput();
    }
  }, []);

  const handleSearchTypeChecked = (checkedSearchType) => {
    if (dominantSearchType === 'rail' && checkedSearchType !== 'rail') {
      reduxDispatch(
        openAlert({
          title: t(tKey('OptionChangeTitleRail')),
          message: t(tKey('OptionChangeMessageRail')),
          confirmText: t('common:Continue'),
          cancelText: t('common:Cancel'),
          onConfirmPressed: () => {
            updateSearchType(checkedSearchType);
            dispatch({ type: 'clearCities' });
          },
        })
      );
      return;
    }
    if (
      dominantSearchType !== '' &&
      dominantSearchType !== 'rail' &&
      checkedSearchType === 'rail'
    ) {
      reduxDispatch(
        openAlert({
          title: t(tKey('OptionChangeTitle')),
          message: t(tKey('OptionChangeMessage')),
          confirmText: t('common:Continue'),
          cancelText: t('common:Cancel'),
          onConfirmPressed: () => {
            updateSearchType(checkedSearchType);
            dispatch({ type: 'clearCities' });
          },
        })
      );
      return;
    }
    updateSearchType(checkedSearchType);
  };

  const updateSearchType = (checkedSearchType) => {
    const componentMap = {
      air: () => dispatch({ type: 'setHasAir' }),
      hotel: () => dispatch({ type: 'setHasHotel' }),
      car: () => dispatch({ type: 'setHasCar' }),
      rail: () => dispatch({ type: 'setHasRail' }),
    };
    componentMap[checkedSearchType]();
  };

  const isNearbyHotelTonightSearch = () => {
    return (
      dominantSearchType === 'hotel' &&
      !hasCar &&
      subSearchType === 'oneway' &&
      geolocation &&
      (!searchCriteria[0].toCity || geolocation.airportCode === searchCriteria[0].toCity) &&
      moment(searchCriteria[0].fromDate).isSame(new Date(), 'day')
    );
  };

  const onSubmit = async () => {
    // copy searchCriteria so we can fill in geolocation and hidden inputs, and if not multicity trim the search criteria length
    let copySearchCriteria = searchCriteria.map((x) => ({ ...x }));
    if (subSearchType !== 'multicity') {
      copySearchCriteria = copySearchCriteria.slice(0, 1);
    }
    const { tripOptions, authNumber, serviceType } = searchInputState;
    const { noOfCars, noOfRooms, persons } = tripOptions ? tripOptions : {};
    const { noOfAdults, noOfSeniors, infants } = persons ? persons : {};
    const searchPayload = {
      authNumber,
      searchCriteria: copySearchCriteria,
      hasAir,
      hasCar,
      hasHotel,
      hasRail,
      airRequestType: searchSubTypeToAirRequestTypeMap[subSearchType] || 0,
      airClassType,
      noofCar: noOfCars,
      noofRooms: noOfRooms,
      adultCount: noOfAdults,
      seniorcount: noOfSeniors,
      infants,
      IsGeoAutocomplete: searchInputState.isGeoSearch, //GR
      isGeoSearch: false, //HT
      latitude: undefined, //HT
      longitude: undefined, //HT
      IsGeoFromSearch: searchInputState.isGeoFromSearch,
      IsGeoToSearch: searchInputState.isGeoToSearch,
      serviceType,
    };
    // isGeoSearch, lat & long are only used for Nearby Hotel Tonight Searches
    if (isNearbyHotelTonightSearch()) {
      searchPayload.isGeoSearch = true;
      searchPayload.latitude = geolocation.latitude;
      searchPayload.longitude = geolocation.longitude;

      if (hasHotel && !hasAir && !hasCar && !hasRail && locationDetail) {
        searchPayload.searchCriteria[0].toCity = locationDetail.label;
        searchPayload.searchCriteria[0].fromCity = locationDetail.label;
        searchPayload.IsGeoToSearch = true;

        const googleGeoObj = await getAddress(locationDetail.label);
        searchPayload.GooglePlace = await transformGoogleAddress(googleGeoObj);
      } else {
        if (geolocation?.airportCode) {
          searchPayload.searchCriteria[0].toCity = geolocation.airportCode;
          searchPayload.searchCriteria[0].fromCity = geolocation.airportCode;
        }
      }
    } else if (geolocation?.airportCode) {
      if (subSearchType !== 'multicity') {
        if (!searchPayload.searchCriteria[0].toCity) {
          __DEV__ && console.info('setting TO city from geolocation');
          searchPayload.searchCriteria[0].toCity = geolocation.airportCode;
        }

        if (!searchPayload.searchCriteria[0].fromCity) {
          __DEV__ && console.info('setting FROM city from geolocation');
          searchPayload.searchCriteria[0].fromCity = geolocation.airportCode;
        }
      }
    }

    // for one way air and rail only we make sure the dates are the same
    if ((hasAir || hasRail) && subSearchType === 'oneway' && !hasHotel && !hasCar) {
      __DEV__ && console.info('setting TO date to FROM date for one way air');
      searchPayload.searchCriteria[0].toDate = searchPayload.searchCriteria[0].fromDate;
    }
    // for hotel we make sure both stations are set the same
    if (subSearchType === 'oneway' && dominantSearchType === 'hotel') {
      __DEV__ && console.info('setting FROM city to TO city for one way hotel');
      searchPayload.searchCriteria[0].fromCity = searchPayload.searchCriteria[0].toCity;
      searchPayload.searchCriteria[0].departParentKey =
        searchPayload.searchCriteria[0].arrivalParentKey;
      searchPayload.searchCriteria[0].departRegionId =
        searchPayload.searchCriteria[0].arrivalRegionId;

      if (searchPayload.IsGeoToSearch) {
        const googleGeoObj = await getAddress(searchPayload.searchCriteria[0].toCity);
        searchPayload.GooglePlace = await transformGoogleAddress(googleGeoObj);
      }
    }
    // for same drop off car we make sure stations are set the same && also set the region parameters
    if (
      dominantSearchType === searchTypesEnum.CAR &&
      subSearchType === subSearchTypeEnum.roundTrip
    ) {
      if (EVENTS_ENABLED) {
        if (searchPayload.searchCriteria[0].fromCity === '') {
          searchPayload.searchCriteria[0].fromCity = searchPayload.searchCriteria[0].toCity;
        } else {
          searchPayload.searchCriteria[0].toCity = searchPayload.searchCriteria[0].fromCity;
        }
      } else {
        searchPayload.searchCriteria[0].toCity = searchPayload.searchCriteria[0].fromCity;
      }
      __DEV__ && console.info('setting TO city to FROM city for same drop off car');
      searchPayload.searchCriteria[0].arrivalRegionId =
        searchPayload.searchCriteria[0].departRegionId;
      searchPayload.searchCriteria[0].arrivalParentKey =
        searchPayload.searchCriteria[0].departParentKey;
      searchPayload.searchCriteria[0].isDifferentDropoff = false;
      if (searchPayload.IsGeoFromSearch) {
        const googleGeoObj = await getAddress(searchPayload.searchCriteria[0].fromCity);
        searchPayload.GooglePlace = await transformGoogleAddress(googleGeoObj);
      }
    }

    if (dominantSearchType === searchTypesEnum.CAR && subSearchType === subSearchTypeEnum.oneWay) {
      searchPayload.searchCriteria[0].isDifferentDropoff = true;

      if (searchPayload.IsGeoFromSearch) {
        const googleGeoObj = await getAddress(searchPayload.searchCriteria[0].fromCity);
        searchPayload.GooglePlace = await transformGoogleAddress(googleGeoObj);
      }

      if (searchPayload.IsGeoToSearch) {
        const googleGeoToCity = await getAddress(searchPayload.searchCriteria[0].toCity);
        searchPayload.GoogleGeoToCity = await transformGoogleAddress(googleGeoToCity);
      }
    }
    // clear pick up times if no car in search
    if (!hasCar) {
      searchPayload.searchCriteria[0].carDropOffTime = '';
      searchPayload.searchCriteria[0].carPickUpTime = '';
      searchPayload.searchCriteria[0].isDifferentDropoff = undefined;
    }
    __DEV__ && console.info(searchPayload);
    onSearch(searchPayload, searchInputState);
  };

  function handleSearchSubmit() {
    if (
      dominantSearchType === searchTypesEnum.AIR &&
      subSearchType === subSearchTypeEnum.oneWay &&
      showHomepagePopupForOneway &&
      homepagePopupForOneWayDetails?.length > 0
    ) {
      navigation.navigate(Modals.HomepageOnewayModal, {
        onSubmit: onSubmit,
      });
    } else {
      onSubmit();
    }
  }

  return [
    {
      searchInputState,
      isNearbyHotelTonightSearch,
      handleSearchTypeChecked,
      handleSearchSubmit,
    },
    dispatch,
  ];
};

export default useTripSearchWidget;
